import React, { useState, useEffect } from 'react';
import TimezoneSelect from 'react-timezone-select';
import Button from '../Button';
import { calculateFirstMatchDate, calculateLastMatchDate, Match } from '../../utils/leagueDates';
import axios from 'axios';

interface TeamFormData {
  name: string;
  league: string;
  section: string;
  district: string;
  flight: string;
  league_start_date: string;
  league_end_date: string;
  timezone: string;
  max_combined_ntrp_rating: number | null;
  match_win_criteria: string | null;
  use_first_match_for_start_date: boolean;
  use_last_match_for_end_date: boolean;
}

interface TeamInformationStepProps {
  initialData: TeamFormData;
  subscriptionCapacity: {
    max_active_teams: number;
    active_teams_count: number;
    remaining_capacity: number;
  } | null;
  onNext: (data: TeamFormData) => void;
  teamId?: number; // Optional team ID for fetching matches if editing existing team
}

const TeamInformationStep: React.FC<TeamInformationStepProps> = ({
  initialData,
  onNext,
  teamId,
}) => {
  // Detect browser timezone
  const getBrowserTimezone = (): string => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return 'America/Los_Angeles';
    }
  };

  const [formData, setFormData] = useState<TeamFormData>({
    ...initialData,
    timezone: initialData.timezone || getBrowserTimezone(),
    use_first_match_for_start_date: initialData.use_first_match_for_start_date ?? false,
    use_last_match_for_end_date: initialData.use_last_match_for_end_date ?? false,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof TeamFormData, string>>>({});
  const [matches, setMatches] = useState<Match[]>([]);

  // Beta-pause: subscription capacity toast disabled.

  // Fetch matches if teamId is provided
  useEffect(() => {
    if (teamId) {
      axios.get<Match[]>(`/matches/teams/${teamId}/matches`)
        .then(response => {
          setMatches(response.data);
        })
        .catch(error => {
          console.error('Failed to fetch matches:', error);
        });
    }
  }, [teamId]);

  // Calculate dates from matches when flags are true
  useEffect(() => {
    if (formData.use_first_match_for_start_date && matches.length > 0) {
      const calculatedDate = calculateFirstMatchDate(matches);
      setFormData(prev => {
        // Only update if the date would actually change
        if (prev.league_start_date !== calculatedDate) {
          return { ...prev, league_start_date: calculatedDate };
        }
        return prev;
      });
    }

    if (formData.use_last_match_for_end_date && matches.length > 0) {
      const calculatedDate = calculateLastMatchDate(matches);
      setFormData(prev => {
        // Only update if the date would actually change
        if (prev.league_end_date !== calculatedDate) {
          return { ...prev, league_end_date: calculatedDate };
        }
        return prev;
      });
    }
  }, [matches, formData.use_first_match_for_start_date, formData.use_last_match_for_end_date]);

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof TeamFormData, string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Team name is required';
    }

    if (!formData.timezone) {
      newErrors.timezone = 'Timezone is required';
    }

    // League dates validation: check that end date is after start date when both are available
    // Get effective start date (calculated or manual)
    let effectiveStartDate: string | null = null;
    if (formData.use_first_match_for_start_date && matches.length > 0) {
      effectiveStartDate = calculateFirstMatchDate(matches);
    } else if (!formData.use_first_match_for_start_date) {
      effectiveStartDate = formData.league_start_date || null;
    }
    
    // Get effective end date (calculated or manual)
    let effectiveEndDate: string | null = null;
    if (formData.use_last_match_for_end_date && matches.length > 0) {
      effectiveEndDate = calculateLastMatchDate(matches);
    } else if (!formData.use_last_match_for_end_date) {
      effectiveEndDate = formData.league_end_date || null;
    }
    
    // Validate that end date is after start date when both are provided
    if (effectiveStartDate && effectiveEndDate) {
      const startDate = new Date(effectiveStartDate);
      const endDate = new Date(effectiveEndDate);
      if (endDate <= startDate) {
        newErrors.league_end_date = 'End date must be after start date';
        // Also set start date error if it's manually set
        if (!formData.use_first_match_for_start_date) {
          newErrors.league_start_date = 'Start date must be before end date';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onNext(formData);
    }
  };

  // Beta-pause: subscription gating disabled.
  const canProceed = true;

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">Step 1: Team Information</h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Team Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
              errors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
            }`}
            placeholder="Enter team name"
            disabled={!canProceed}
          />
          {errors.name && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.name}</p>}
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Only shown to your team and can be fun or serious
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="league" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              League (optional)
            </label>
            <input
              type="text"
              id="league"
              value={formData.league}
              onChange={(e) => setFormData({ ...formData, league: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="e.g., USTA League"
              disabled={!canProceed}
            />
          </div>

          <div>
            <label htmlFor="section" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Section (optional)
            </label>
            <input
              type="text"
              id="section"
              value={formData.section}
              onChange={(e) => setFormData({ ...formData, section: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="e.g., Southern"
              disabled={!canProceed}
            />
          </div>

          <div>
            <label htmlFor="district" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              District (optional)
            </label>
            <input
              type="text"
              id="district"
              value={formData.district}
              onChange={(e) => setFormData({ ...formData, district: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="e.g., Georgia"
              disabled={!canProceed}
            />
          </div>

          <div>
            <label htmlFor="flight" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Flight (optional)
            </label>
            <input
              type="text"
              id="flight"
              value={formData.flight}
              onChange={(e) => setFormData({ ...formData, flight: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder="e.g., 4.5"
              disabled={!canProceed}
            />
          </div>
        </div>

        {/* League Dates */}
        <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="mb-4">
            <h4 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">League Dates</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Dates are used to determine if your team is currently active.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <input
                  type="checkbox"
                  id="use_first_match"
                  checked={formData.use_first_match_for_start_date}
                  onChange={(e) => {
                    const useCalculated = e.target.checked;
                    setFormData({ 
                      ...formData, 
                      use_first_match_for_start_date: useCalculated,
                      // Clear manual date when using calculated date
                      league_start_date: useCalculated ? '' : formData.league_start_date
                    });
                    // Clear errors when toggling
                    if (errors.league_start_date) {
                      setErrors({ ...errors, league_start_date: undefined });
                    }
                  }}
                  className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  disabled={!canProceed}
                />
                <label htmlFor="use_first_match" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Use first match date
                </label>
              </div>
            {!formData.use_first_match_for_start_date && (
              <>
                <label htmlFor="league_start_date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  League Start Date (optional)
                </label>
                <input
                  type="date"
                  id="league_start_date"
                  value={formData.league_start_date}
                  onChange={(e) => setFormData({ ...formData, league_start_date: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                    errors.league_start_date ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                  disabled={!canProceed}
                />
              </>
            )}
            {formData.use_first_match_for_start_date && matches.length === 0 && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Date will be calculated when matches are added
              </p>
            )}
            {errors.league_start_date && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.league_start_date}</p>
            )}
          </div>

          <div>
            <div className="flex items-center gap-2 mb-1">
              <input
                type="checkbox"
                id="use_last_match"
                checked={formData.use_last_match_for_end_date}
                onChange={(e) => {
                  const useCalculated = e.target.checked;
                  setFormData({ 
                    ...formData, 
                    use_last_match_for_end_date: useCalculated,
                    // Clear manual date when using calculated date
                    league_end_date: useCalculated ? '' : formData.league_end_date
                  });
                  // Clear errors when toggling
                  if (errors.league_end_date) {
                    setErrors({ ...errors, league_end_date: undefined });
                  }
                }}
                className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                disabled={!canProceed}
              />
              <label htmlFor="use_last_match" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Use last match date
              </label>
            </div>
            {!formData.use_last_match_for_end_date && (
              <>
                <label htmlFor="league_end_date" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  League End Date (optional)
                </label>
                <input
                  type="date"
                  id="league_end_date"
                  value={formData.league_end_date}
                  onChange={(e) => setFormData({ ...formData, league_end_date: e.target.value })}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                    errors.league_end_date ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                  disabled={!canProceed}
                />
              </>
            )}
            {formData.use_last_match_for_end_date && matches.length === 0 && (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Date will be calculated when matches are added
              </p>
            )}
            {errors.league_end_date && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.league_end_date}</p>
            )}
          </div>
          </div>
        </div>

        <div>
          <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Timezone <span className="text-red-500">*</span>
          </label>
          <TimezoneSelect
            value={formData.timezone}
            onChange={(tz) => setFormData({ ...formData, timezone: tz.value })}
            className={`timezone-select ${errors.timezone ? 'border-red-500 dark:border-red-400' : ''}`}
            classNames={{
              control: (state) => `!bg-white !border-gray-300 dark:!bg-gray-700 dark:!border-gray-600 dark:!text-gray-100 ${state.isFocused ? '!border-green-500 !ring-green-500 dark:!border-green-400 dark:!ring-green-400' : ''} ${errors.timezone ? '!border-red-500 dark:!border-red-400' : ''}`,
              singleValue: () => '!text-gray-900 dark:!text-gray-100',
              input: () => '!text-gray-900 dark:!text-gray-100',
              menu: () => '!bg-white dark:!bg-gray-700 !border-gray-300 dark:!border-gray-600',
              option: (state) => `!text-gray-900 dark:!text-gray-100 ${state.isFocused ? '!bg-green-100 dark:!bg-green-900/20' : ''} ${state.isSelected ? '!bg-green-500 dark:!bg-green-600 !text-white' : ''}`,
              placeholder: () => '!text-gray-400 dark:!text-gray-500',
              menuList: () => 'dark:!bg-gray-700',
              indicatorSeparator: () => '!bg-gray-300 dark:!bg-gray-600',
              dropdownIndicator: () => '!text-gray-400 dark:!text-gray-400',
              clearIndicator: () => '!text-gray-400 dark:!text-gray-400',
            }}
            isDisabled={!canProceed}
          />
          {errors.timezone && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.timezone}</p>
          )}
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Used for all matches and calendar events for this team
          </p>
        </div>

        <div>
          <label htmlFor="max_combined_ntrp_rating" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Max Combined NTRP Rating (optional)
          </label>
          <select
            id="max_combined_ntrp_rating"
            value={formData.max_combined_ntrp_rating || ''}
            onChange={(e) => setFormData({ ...formData, max_combined_ntrp_rating: e.target.value ? parseFloat(e.target.value) : null })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            disabled={!canProceed}
          >
            <option value="">Select a rating (optional)</option>
            {Array.from({ length: 26 }, (_, i) => {
              const value = 1.5 + (i * 0.5);
              return (
                <option key={value} value={value}>
                  {value.toFixed(1)}
                </option>
              );
            })}
          </select>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Maximum combined NTRP rating for doubles lineups (e.g., 6.0 means two players' ratings must sum to 6.0 or less)
          </p>
        </div>

        <div>
          <label htmlFor="match_win_criteria" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Match Win Criteria (optional)
          </label>
          <select
            id="match_win_criteria"
            value={formData.match_win_criteria || ''}
            onChange={(e) => setFormData({ ...formData, match_win_criteria: e.target.value || null })}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            disabled={!canProceed}
          >
            <option value="">Not set</option>
            <option value="team_wins">Team Wins</option>
            <option value="total_games">Total Games</option>
          </select>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            How the match winner is determined. Team Wins: team winning the most individual lineups. Total Games: sum of individual games won across all lineups.
          </p>
        </div>

        <div className="flex justify-end pt-4">
          <Button
            type="submit"
            variant="primary"
            disabled={!canProceed}
          >
            Next
          </Button>
        </div>
      </form>
    </div>
  );
};

export default TeamInformationStep;

